import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/Sidebar';
import {
  isMobileSidebarTabItem,
  type MobileTabItem,
} from './mobile-tab-nav-config';

function isMobileTabActive(pathname: string, href: string): boolean {
  if (href === '/admin/dashboard') {
    return pathname === '/admin/dashboard' || pathname === '/dashboard';
  }
  if (href.endsWith('/lessons')) {
    const academyBase = href.replace(/\/lessons$/, '');
    return (
      pathname === href ||
      pathname.startsWith(`${href}/`) ||
      pathname.startsWith(`${academyBase}/location/`)
    );
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export interface DashboardMobileTabBarProps {
  items: MobileTabItem[];
}

export function DashboardMobileTabBar({ items }: DashboardMobileTabBarProps) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const { toggleSidebar, openMobile } = useSidebar();

  if (!isMobile || items.length === 0) {
    return null;
  }

  const tabClassName = (isActive: boolean) =>
    cn(
      'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-2 text-[10px] font-medium transition-colors',
      isActive
        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
    );

  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden"
      aria-label="Quick navigation"
    >
      <div className="pointer-events-auto flex w-full max-w-md items-stretch justify-around gap-0.5 rounded-2xl border border-border bg-card/95 px-1 py-1.5 shadow-lg backdrop-blur-md supports-[backdrop-filter]:bg-card/80">
        {items.map((item) => {
          if (isMobileSidebarTabItem(item)) {
            const Icon = item.icon;
            return (
              <button
                key="open-sidebar"
                type="button"
                aria-label={item.label}
                aria-expanded={openMobile}
                onClick={() => toggleSidebar()}
                className={tabClassName(openMobile)}
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                <span className="truncate leading-none">{item.mobileLabel ?? item.label}</span>
              </button>
            );
          }

          const Icon = item.icon;
          const isActive = isMobileTabActive(pathname, item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              {...(item.tourId ? { id: item.tourId } : {})}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              className={tabClassName(isActive)}
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              <span className="truncate leading-none">{item.mobileLabel ?? item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
